import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { getApiUrl } from "@/lib/api-config";
import { StandardButton, SaveChangesButton, CancelButton } from "@/styles/StandardButtons";
import { setHasUnsavedChanges } from "@/lib/unsaved-changes";
import { PERMISSIONS_CHANGED_EVENT } from "@/contexts/PermissionsContext";

/**
 * Settings > Permissions -- Role & Permissions system (2026-09-15). Resources are grouped by section (collapsible, all collapsed at first); each resource is a
 * small matrix with the ROLES as rows (Admin, Member, Vendor, Client) and the OPERATIONS as columns (View, Create, Update, Delete, then any extra).
 * Shows every configurable permission (resource x action) as a checkbox per
 * fixed role (Admin/Member/Vendor/Client), fetched from the backend
 * catalog + matrix -- nothing here is hardcoded, adding a resource/action
 * on the backend shows up here automatically on next load.
 *
 * Immutable permissions (currently `users.change_role` and `users.remove`
 * -- see backend/core/permissions-catalog.js) render as a locked, always
 * "Admin only" row instead of checkboxes: they're never governed by
 * role_permissions, so a toggle here would be misleading.
 *
 * Read-only for everyone; only Admin can save changes (mirrors the
 * backend's own admin-only PUT /api/permissions/matrix -- this is a UI
 * convenience, not the real enforcement).
 */

type Resource = { label: string; section?: string; actions: string[] };
type Catalog = {
  roles: string[];
  resources: Record<string, Resource>;
  permissions: string[];
  immutable_permissions: string[];
  /** The only permissions an admin may grant to Vendor / Client (the server refuses any other); a missing list means none. */
  external_grantable_permissions?: string[];
};
type Matrix = Record<string, string[]>;

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  member: "Member",
  vendor: "Vendor",
  client: "Client",
};

// Rows of the matrix: the roles, in this order (a role the catalog does not know yet would be appended)
const ROLE_ORDER = ["admin", "member", "vendor", "client"];
// Columns: the four operations first, then whatever else a resource offers (invite, upload, view builder price...)
const STANDARD_ACTIONS = ["view", "create", "update", "delete"];
const WRITE_ACTIONS = ["create", "update", "delete"];
const actionLabel = (action: string) => (action === "view" ? "View" : action === "create" ? "Create" : action === "update" ? "Update" : action === "delete" ? "Delete" : action.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()));
const orderedActions = (actions: string[]) => [...STANDARD_ACTIONS.filter((a) => actions.includes(a)), ...actions.filter((a) => !STANDARD_ACTIONS.includes(a))];
const SECTION_ORDER = ["Projects", "Project Management", "Cost Management", "Leads", "People", "Drive"];

export function PermissionsTab() {
  const { userRole } = useUser();
  const isAdmin = userRole === "admin";

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [matrix, setMatrix] = useState<Matrix>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Belt and braces against a double click firing two saves before React re-renders the disabled button
  const savingRef = useRef(false);

  // Pending edits, keyed "role|permission" -> granted. Applied on top of
  // `matrix` for rendering, flushed to the backend (and cleared) on Save.
  const [pending, setPending] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [catalogRes, matrixRes] = await Promise.all([
        fetch(getApiUrl("api/permissions/catalog")),
        fetch(getApiUrl("api/permissions/matrix")),
      ]);
      const catalogJson = await catalogRes.json().catch(() => null);
      const matrixJson = await matrixRes.json().catch(() => null);

      if (!catalogRes.ok || !catalogJson?.success) {
        throw new Error(catalogJson?.error || "Failed to load permission catalog");
      }
      if (!matrixRes.ok || !matrixJson?.success) {
        throw new Error(matrixJson?.error || "Failed to load current permissions");
      }

      setCatalog(catalogJson.data);
      setMatrix(matrixJson.data);
      setPending({});
    } catch (error) {
      console.error("Error loading permissions:", error);
      setLoadError(error instanceof Error ? error.message : "Failed to load permissions");
    } finally {
      setIsLoading(false);
    }
  };

  const isGranted = (role: string, permission: string): boolean => {
    const key = `${role}|${permission}`;
    if (key in pending) return pending[key];
    return (matrix[role] || []).includes(permission);
  };

  const isInMatrix = (role: string, permission: string) => (matrix[role] || []).includes(permission);

  // Records the wanted state of ONE cell. A cell set back to what is saved is no longer a pending change.
  const withCell = (current: Record<string, boolean>, role: string, permission: string, granted: boolean) => {
    const key = `${role}|${permission}`;
    const next = { ...current };
    if (granted === isInMatrix(role, permission)) delete next[key];
    else next[key] = granted;
    return next;
  };

  const toggle = (role: string, permission: string) => {
    if (!isAdmin) return;
    setSaveSuccess(false);
    setSaveError(null);
    const granted = !isGranted(role, permission);
    setPending((prev) => {
      const [resourceKey, action] = [permission.slice(0, permission.lastIndexOf(".")), permission.slice(permission.lastIndexOf(".") + 1)];
      let next = withCell(prev, role, permission, granted);
      // Turning View off takes Create, Update and Delete of the SAME resource and role with it (a write cannot exist without view). Turning View back on gives nothing back.
      if (action === "view" && !granted) {
        for (const writeAction of WRITE_ACTIONS) {
          const writePermission = `${resourceKey}.${writeAction}`;
          if (!catalog?.resources[resourceKey]?.actions.includes(writeAction)) continue;
          const writeKey = `${role}|${writePermission}`;
          const currentlyOn = writeKey in next ? next[writeKey] : isInMatrix(role, writePermission);
          if (currentlyOn) next = withCell(next, role, writePermission, false);
        }
      }
      return next;
    });
  };

  const hasChanges = Object.keys(pending).length > 0;

  useEffect(() => {
    setHasUnsavedChanges(hasChanges);
  }, [hasChanges]);

  useEffect(() => {
    return () => setHasUnsavedChanges(false);
  }, []);

  const handleSave = async () => {
    if (!catalog || !hasChanges || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const changes = Object.entries(pending).map(([key, granted]) => {
        const [role, permission] = key.split("|");
        return { role, permission, granted };
      });

      const response = await fetch(getApiUrl("api/permissions/matrix"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ changes }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(typeof result?.error === "string" && result.error ? result.error : "Failed to save permissions");
      }

      setMatrix(result.data);
      setPending({});
      // only now, after the server confirmed
      setSaveSuccess(true);
      // the navigation of the admin's own role may have changed
      window.dispatchEvent(new Event(PERMISSIONS_CHANGED_EVENT));
    } catch (error) {
      console.error("Error saving permissions:", error);
      setSaveError(error instanceof Error ? error.message : "Failed to save permissions");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setPending({});
    setSaveError(null);
    setSaveSuccess(false);
  };

  // The resources grouped by section (collapsible headings, ALL collapsed when the tab opens)
  const sections = useMemo(() => {
    if (!catalog) return [] as Array<{ name: string; resources: Array<[string, Resource]> }>;
    const bySection = new Map<string, Array<[string, Resource]>>();
    for (const entry of Object.entries(catalog.resources)) {
      const name = entry[1].section || "Other";
      bySection.set(name, [...(bySection.get(name) || []), entry]);
    }
    return Array.from(bySection.entries())
      .sort(([a], [b]) => (SECTION_ORDER.indexOf(a) + 1 || 99) - (SECTION_ORDER.indexOf(b) + 1 || 99))
      .map(([name, resources]) => ({ name, resources }));
  }, [catalog]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const toggleSection = (name: string) => setExpanded((current) => (current.includes(name) ? current.filter((n) => n !== name) : [...current, name]));
  const roleRows = useMemo(() => {
    const known = catalog ? catalog.roles : [];
    return [...ROLE_ORDER.filter((r) => known.includes(r)), ...known.filter((r) => !ROLE_ORDER.includes(r))];
  }, [catalog]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[24rem]">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (loadError || !catalog) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-600">{loadError || "Failed to load permissions"}</p>
        <StandardButton type="button" customVariant="white" onClick={fetchAll} className="mt-4">
          Retry
        </StandardButton>
      </div>
    );
  }

  return (
    <div>
      {!isAdmin && (
        <p className="mb-4 text-sm text-gray-500">
          Only company admins can change permissions. You can view the current matrix below.
        </p>
      )}
      <p className="mb-4 text-sm text-gray-500" data-testid="permissions-company-scope">
        These permissions apply only to your company. Vendor and Client access is not configurable yet.
      </p>
      {saveSuccess && <p className="mb-4 text-sm text-green-600" role="status">Permissions saved successfully.</p>}
      {saveError && <p className="mb-4 text-sm text-red-600" role="alert">{saveError}</p>}

      <div className="space-y-3" data-testid="permission-sections">
        {sections.map((section) => {
          const open = expanded.includes(section.name);
          return (
            <div key={section.name} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`permission-section-${section.name}`}>
              <button
                type="button"
                className="flex w-full items-center gap-2 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-100"
                aria-expanded={open}
                onClick={() => toggleSection(section.name)}
              >
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {section.name}
                <span className="font-normal text-gray-400">({section.resources.length})</span>
              </button>
              {open && (
                <div className="divide-y divide-gray-100">
                  {section.resources.map(([resourceKey, resource]) => {
                    const actions = orderedActions(resource.actions);
                    return (
                      <div key={resourceKey} className="px-4 py-3 overflow-x-auto" data-testid={`permission-resource-${resourceKey}`}>
                        <table className="min-w-full text-sm">
                          <caption className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-2">{resource.label}</caption>
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th scope="col" className="text-left px-3 py-2 font-medium text-gray-700 w-32">Role</th>
                              {actions.map((action) => (
                                <th key={action} scope="col" className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{actionLabel(action)}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {roleRows.map((role) => (
                              <tr key={role} className="border-b border-gray-100 last:border-b-0">
                                <th scope="row" className="text-left px-3 py-2 font-normal text-gray-700">{ROLE_LABELS[role] || role}</th>
                                {actions.map((action) => {
                                  const permission = `${resourceKey}.${action}`;
                                  const isImmutable = catalog.immutable_permissions.includes(permission);
                                  if (isImmutable) {
                                    return (
                                      <td key={action} className="px-3 py-2 text-center" title="Structural security rule -- always Admin only, cannot be changed here">
                                        {role === "admin" ? (
                                          <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3 w-3" />Always</span>
                                        ) : (
                                          <span className="text-xs text-gray-300">—</span>
                                        )}
                                      </td>
                                    );
                                  }
                                  const external = role === "vendor" || role === "client";
                                  const externalLocked = external && !(catalog.external_grantable_permissions || []).includes(permission);
                                  const lockedTitle = externalLocked
                                    ? "Not available to Vendor and Client: this access cannot be granted to external accounts"
                                    : external
                                      ? "Only the records assigned to or shared with this person are visible, even with this on"
                                      : undefined;
                                  return (
                                    <td key={action} className="px-3 py-2 text-center" title={lockedTitle}>
                                      <input
                                        type="checkbox"
                                        aria-label={`${ROLE_LABELS[role] || role}: ${resource.label} ${actionLabel(action)}`}
                                        checked={isGranted(role, permission)}
                                        disabled={!isAdmin || externalLocked}
                                        onChange={() => toggle(role, permission)}
                                        className="h-4 w-4 rounded border-gray-300 focus:outline-none focus:ring-0"
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div className="mt-6 flex items-center justify-end gap-2">
          <CancelButton onClick={handleDiscard} disabled={isSaving} />
          <SaveChangesButton type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </SaveChangesButton>
        </div>
      )}
    </div>
  );
}
